import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type MouseEvent, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { formatDateTime } from '@shared/format/datetime';
import { Avatar } from '@centraoneplus/ui';
import { Lock, Paperclip, Bot, Sparkles, BookOpen, CheckCircle2, MessageSquare, Mail, Phone, MessageCircle, ChevronRight, ChevronDown, MoreHorizontal, Languages, Loader2 } from 'lucide-react';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { EmailHtmlFrame } from './EmailHtmlFrame';
import { MessageEmotionChip } from './sentiment/MessageEmotionChip';
import type { MessageSentiment } from './sentiment/emotionConfig';
import {
  sortMessagesAscending,
  isCollapsibleSystemNotification,
  filterDisplayableMessages,
  splitTrimmedHtml,
  splitTrimmedText,
  findCidAttachmentFileId,
  collectInlineCidFileIds,
} from './email-render.utils';
import { fileService } from '@services/file.service';
import type { Message } from '@centraoneplus/shared-types';
import { aiService } from '@services/ai.service';
import { languageName } from './voiceTranscript';
import { messagePlainText, untranslated, type MessageTranslation } from './messageTranslation';
import { sanitizeMessageHtml } from '@centraoneplus/ui';
import {
  readVoiceTurn,
  threadHasTranslations,
  displayedText,
  showsOriginalBeneath,
} from './voiceTranscript';

/**
 * Download a message attachment.
 *
 * Attachment bytes live in the file-service and are fetched through the
 * authenticated `/api/files/<fileId>/download` proxy (the proxy adds the auth
 * token and streams the real bytes). The raw `attachment.url` is a private
 * storage URL (e.g. an Azure blob link) the browser can't open directly, so we
 * always prefer the fileId when it's present.
 */
function downloadMessageAttachment(
  e: MouseEvent,
  attachment: { url?: string; name?: string; fileId?: string },
): void {
  const fileId = attachment.fileId;
  if (!fileId) return; // no fileId → fall back to the href (raw url)
  e.preventDefault();
  void fileService.downloadAttachment({
    url: `/api/files/${fileId}/download`,
    name: attachment.name || 'download',
  });
}

interface ConversationThreadProps {
  messages: Message[];
  isLoading?: boolean;
  isChatTicket?: boolean;
  ticketChannel?: string;
  /** What the call was about. Shown above the turns, for voice tickets. */
  voiceSummary?: string;
  /**
   * The ticket contact. An inbound email from any other address (e.g. someone
   * the agent CC'd) is labelled with its sender. Omitted → no label.
   */
  contactEmail?: string;
  /**
   * Email replies still inside their undo window. Shown at the end of the
   * thread, as they will look once sent, marked as not sent yet.
   */
  pendingReplies?: PendingReply[];
}

export interface PendingReply {
  /** The reply rendered like a sent message (agent, email, To/CC, attachments). */
  message: Message;
  /** "Sending in 23s" / "Sending…" / "Cancelling…". */
  statusLabel: string;
}

/**
 * Renders AI/Bot message content with attractive formatting
 */
function AIMessageContent({ content }: { content: string }) {
  // Parse the HTML content to extract sections
  const sections = parseAIContent(content);

  return (
    <div className="space-y-3">
      {/* Greeting */}
      {sections.greeting && (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {sections.greeting}
        </p>
      )}

      {/* AI Badge */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-[var(--co-machine-wash)] border border-[var(--co-machine-line)] rounded-full">
          <Sparkles className="h-3 w-3 text-[var(--co-machine-ink)]" />
          <span className="text-xs font-medium text-[var(--co-machine-ink)]">
            AI Generated Response
          </span>
        </div>
      </div>

      {/* Introduction text */}
      {sections.intro && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {sections.intro}
        </p>
      )}

      {/* Main Knowledge Base Content */}
      {sections.mainContent && (
        <div className="relative">
          <div className="relative bg-[var(--co-raised)] rounded-xl border border-[var(--co-machine-line)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--co-machine-wash)] border-b border-[var(--co-machine-line)]">
              <Bot className="h-4 w-4 text-[var(--co-machine-ink)]" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Knowledge Base Answer
              </span>
            </div>

            {/* Content */}
            <div className="p-4">
              <div
                className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed
                  [&>p]:mb-3 [&>p:last-child]:mb-0
                  [&_br]:block [&_br]:h-1"
                dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(formatContent(sections.mainContent)) }}
              />
            </div>

            {/* Source citation */}
            {sections.citations.length > 0 && (
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <CheckCircle2 className="h-3 w-3 text-[var(--co-green)]" />
                  <span>Sourced from {sections.citations.length} knowledge base article{sections.citations.length > 1 ? 's' : ''}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Related Articles */}
      {sections.relatedArticles.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-4 w-4 text-[var(--co-machine-ink)]" />
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Related Articles
            </span>
          </div>
          <div className="grid gap-2">
            {sections.relatedArticles.map((article, idx) => (
              <a
                key={idx}
                href={article.url || '#'}
                className="flex items-center gap-3 p-3 bg-[var(--co-machine-wash)] rounded-lg border border-[var(--co-machine-line)] hover:border-[var(--co-machine)] transition-colors group"
              >
                <div className="flex-shrink-0 w-6 h-6 bg-[var(--co-machine-ink)] rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-[var(--co-on-ink)]">{idx + 1}</span>
                </div>
                <span className="text-sm text-[var(--co-machine-ink)] font-medium group-hover:underline">
                  {article.title}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Closing message */}
      {sections.closing && (
        <p className="text-sm text-gray-600 dark:text-gray-400 italic pt-2 border-t border-gray-100 dark:border-gray-700">
          {sections.closing}
        </p>
      )}
    </div>
  );
}

interface ParsedSections {
  greeting: string;
  intro: string;
  mainContent: string;
  relatedArticles: Array<{ title: string; url?: string }>;
  citations: string[];
  closing: string;
}

function parseAIContent(html: string): ParsedSections {
  const result: ParsedSections = {
    greeting: '',
    intro: '',
    mainContent: '',
    relatedArticles: [],
    citations: [],
    closing: ''
  };

  // Create temp element to parse
  const temp = document.createElement('div');
  temp.innerHTML = html;

  const children = Array.from(temp.children);

  children.forEach((child) => {
    const text = child.textContent?.trim() || '';
    const tagName = child.tagName.toLowerCase();

    if (tagName === 'p') {
      if (text.match(/^(hi|hello|dear)\s/i)) {
        result.greeting = text;
      } else if (text.toLowerCase().includes('thank you') || text.toLowerCase().includes('based on')) {
        result.intro = text;
      } else if (text.toLowerCase().includes('related articles') || text.includes('<strong>Related')) {
        // Skip - we'll handle related articles separately
      } else if (text.toLowerCase().includes('further questions') || text.toLowerCase().includes('feel free')) {
        result.closing = text;
      }
    } else if (tagName === 'div') {
      // Main content box
      result.mainContent = child.innerHTML;
    } else if (tagName === 'ul' || tagName === 'ol') {
      const items = child.querySelectorAll('li');
      result.relatedArticles = Array.from(items).map(li => {
        const link = li.querySelector('a');
        return {
          title: link?.textContent || li.textContent || '',
          url: link?.href
        };
      });
    }
  });

  // Extract citations
  const citationMatches = html.match(/\[(\d+)\]/g);
  if (citationMatches) {
    result.citations = [...new Set(citationMatches.map(c => c.replace(/[\[\]]/g, '')))];
  }

  // If no structured content, use full HTML as main content
  if (!result.mainContent && !result.greeting) {
    result.mainContent = html;
  }

  return result;
}

function formatContent(html: string): string {
  return html
    .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')
    .replace(/\s*\[\d+\]\s*/g, ' ') // Remove citation markers
    .trim();
}

/** Approximate perceived brightness (0–255) for a CSS color string. */
function colorBrightness(input: string): number | null {
  const s = input.trim().toLowerCase();
  // 3- or 6-digit hex
  let m = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
  }
  // rgb() / rgba()
  m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (m) {
    const r = +m[1], g = +m[2], b = +m[3];
    return (r * 299 + g * 587 + b * 114) / 1000;
  }
  // A few named colors that emails actually use
  if (s === 'black') return 0;
  if (s === 'white') return 255;
  if (s === 'transparent' || s === 'inherit' || s === 'currentcolor') return null;
  return null;
}

/**
 * Strip `color`/`background-color` declarations from an inline `style`
 * attribute *only when* they would produce unreadable contrast against our
 * dark-mode card (near-black text or near-white background). Brand colors
 * (blue links, red flags, etc.) are left alone.
 */
function neutralizeUnreadableColors(el: Element): void {
  const style = el.getAttribute('style');
  if (style) {
    let next = style;
    const colorMatch = /(?:^|;)\s*color\s*:\s*([^;]+)/i.exec(next);
    if (colorMatch) {
      const b = colorBrightness(colorMatch[1]);
      if (b !== null && b < 80) {
        next = next.replace(/(?:^|;)\s*color\s*:\s*[^;]+;?/i, ';');
      }
    }
    const bgMatch = /(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i.exec(next);
    if (bgMatch) {
      const b = colorBrightness(bgMatch[1]);
      if (b !== null && b > 200) {
        next = next.replace(/(?:^|;)\s*background(?:-color)?\s*:\s*[^;]+;?/i, ';');
      }
    }
    next = next.replace(/;{2,}/g, ';').replace(/^;\s*/, '').replace(/;\s*$/, '').trim();
    if (next) el.setAttribute('style', next);
    else el.removeAttribute('style');
  }
  // Legacy HTML4 email attrs that bypass CSS entirely
  const colorAttr = el.getAttribute('color');
  if (colorAttr) {
    const b = colorBrightness(colorAttr);
    if (b !== null && b < 80) el.removeAttribute('color');
  }
  const bgcolorAttr = el.getAttribute('bgcolor');
  if (bgcolorAttr) {
    const b = colorBrightness(bgcolorAttr);
    if (b !== null && b > 200) el.removeAttribute('bgcolor');
  }
}

/**
 * Make email/ticket HTML safe to render in the conversation panel.
 *
 * Three failure modes we handle:
 *   1. `<img src="cid:..">` and src-less imgs — browsers can't render cid:
 *      outside an email client. A cid: image whose attachment bytes were
 *      fetched (useInlineImageUrls) renders from its blob: URL; otherwise we
 *      replace it with a small "inline image (not displayable)" chip.
 *   2. Hardcoded near-black text + near-white backgrounds in the email
 *      HTML — these win against `dark:` Tailwind classes and produce
 *      unreadable black-on-dark output. We strip those *specific*
 *      declarations so the parent's prose-invert color takes effect, while
 *      brand colors (links, accents) are kept.
 *   3. External images get `loading="lazy"` + a no-referrer policy as a
 *      tracker-leak hardening pass.
 */
/** fileId → blob: URL of an inline image; null once its fetch has failed. */
type InlineImageUrls = Record<string, string | null>;

/**
 * CO-004: fetch the bytes of every attachment this message's body shows inline
 * (`<img src="cid:...">`) through the authenticated client. An `<img>` pointed
 * straight at `/api/files/<id>/download` sends no Bearer token and 401s, which
 * is what rendered inline email images as broken icons. No-op (no requests)
 * for messages without cid: images.
 */
function useInlineImageUrls(message: Message): InlineImageUrls {
  const key = useMemo(
    () => collectInlineCidFileIds(message.content, message.attachments).join(','),
    [message.content, message.attachments],
  );
  const [urls, setUrls] = useState<InlineImageUrls>({});

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    for (const fileId of key.split(',')) {
      fileService.getFileObjectUrl(fileId).then(
        (url) => {
          if (!cancelled) setUrls((prev) => (prev[fileId] === url ? prev : { ...prev, [fileId]: url }));
        },
        () => {
          if (!cancelled) setUrls((prev) => ({ ...prev, [fileId]: null }));
        },
      );
    }
    return () => {
      cancelled = true;
    };
  }, [key]);

  return urls;
}

function prepareMessageHtml(
  html: string,
  attachments?: Message['attachments'],
  inlineImageUrls: InlineImageUrls = {},
): string {
  // H-1: SANITIZE FIRST, then do the display passes on the safe output.
  //
  // This function used to be called `sanitizeMessageHtml` and did only the two
  // display passes below — it stripped no <script>, no on* handlers and no
  // javascript: URLs. The name was doing the reassuring, not the code, and
  // ReplyEditor.tsx:71-72 relied on it in writing ("The rendered conversation
  // thread sanitizes again on its side"). Renamed so the name matches what it
  // does, and the actual sanitizing is delegated to the one shared
  // implementation (packages/ui/src/security/sanitize-html.ts).
  //
  // It also used to `return html` from its catch — handing raw markup to
  // dangerouslySetInnerHTML precisely when parsing had gone wrong. The shared
  // sanitizer returns '' instead.
  const safe = sanitizeMessageHtml(html);
  if (!safe) return '';

  try {
    const doc = new DOMParser().parseFromString(safe, 'text/html');

    doc.querySelectorAll('img').forEach((img) => {
      const src = (img.getAttribute('src') || '').trim();
      // #9197 / CO-004: resolve cid: inline images to the attachment's bytes
      // (a blob: URL fetched with auth by useInlineImageUrls) before falling
      // back to the "not displayable" placeholder.
      if (src.toLowerCase().startsWith('cid:')) {
        const fileId = findCidAttachmentFileId(src, attachments);
        const blobUrl = fileId ? inlineImageUrls[fileId] : null;
        if (blobUrl) {
          img.setAttribute('src', blobUrl);
          return;
        }
        if (fileId && blobUrl === undefined) {
          // Still loading: render nothing yet rather than a broken image.
          img.removeAttribute('src');
          img.setAttribute('alt', '');
          return;
        }
      }
      const unrenderable =
        !src ||
        src.toLowerCase().startsWith('cid:') ||
        src.toLowerCase().startsWith('mid:');
      if (unrenderable) {
        const placeholder = doc.createElement('span');
        placeholder.className =
          'inline-flex items-center gap-1 px-2 py-0.5 text-xs italic text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded border border-dashed border-gray-200 dark:border-gray-700';
        placeholder.textContent =
          img.getAttribute('alt')?.trim() || 'Inline image (not displayable)';
        img.replaceWith(placeholder);
        return;
      }
      img.setAttribute('loading', 'lazy');
      img.setAttribute('referrerpolicy', 'no-referrer');
    });

    // Walk every element once for the contrast pass. Cheap (a few hundred
    // nodes at most) and avoids touching React's render path.
    doc.body.querySelectorAll('*').forEach((el) => neutralizeUnreadableColors(el));

    return doc.body.innerHTML;
  } catch {
    // Already sanitized above, so returning `safe` here is not a fail-open -
    // it only loses the cosmetic passes.
    return safe;
  }
}

function getMessageChannel(message: Message, ticketChannel?: string): string {
  return message.metadata?.source || message.metadata?.channel || ticketChannel || 'chat';
}

/**
 * Render an email HTML body. Email bodies are frequently full HTML documents
 * with their own `<style>`/backgrounds/full-width tables, so we render them in
 * a sandboxed iframe (no scripts, isolated CSS) that auto-sizes to content.
 * The legacy inline-prose path was replaced because the email's CSS bled into
 * the app layout.
 */
function EmailBody({ html, title, collapsible }: { html: string; title?: string; collapsible?: boolean }) {
  return <EmailHtmlFrame html={html} title={title} collapsible={collapsible} />;
}

/**
 * Gmail-style trimming for normal customer/agent/system message bodies: show
 * the substantive body, tuck trailing quoted-reply history / signature blocks
 * behind a `···` toggle. Detection is conservative (see email-render.utils) so
 * real content is never hidden.
 *
 * `renderHtml`/`renderText` let the caller keep its own styling (prose div,
 * sandboxed frame, plain <p>) while this component owns only the split + toggle.
 */
function TrimmableContent({
  message,
  isHtml,
  renderHtml,
  renderText,
}: {
  message: Message;
  isHtml: boolean;
  renderHtml: (html: string) => ReactNode;
  renderText: (text: string) => ReactNode;
}) {
  const [showTrimmed, setShowTrimmed] = useState(false);
  const split = useMemo(
    () => (isHtml ? splitTrimmedHtml(message.content) : splitTrimmedText(message.content)),
    [message.content, isHtml],
  );

  const render = isHtml ? renderHtml : renderText;

  if (!split.hasTrimmed) {
    return render(message.content);
  }

  return (
    <div className="min-w-0">
      {render(split.visible)}
      <button
        type="button"
        onClick={() => setShowTrimmed((v) => !v)}
        className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-expanded={showTrimmed}
        title={showTrimmed ? 'Hide trimmed content' : 'Show trimmed content'}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
        <span className="text-[11px] font-medium">
          {showTrimmed ? 'Hide trimmed content' : 'Show trimmed content'}
        </span>
      </button>
      {showTrimmed && (
        <div className="mt-2 pl-3 border-l-2 border-gray-200 dark:border-gray-700 opacity-90">
          {render(split.trimmed)}
        </div>
      )}
    </div>
  );
}

/**
 * Compact, expandable chip for a TEMPLATED system notification (allowlisted
 * `metadata.emailType` — e.g. `ticket_confirmation`). Collapsed by default;
 * expands to the full sandboxed email body.
 *
 * Gated on the emailType allowlist (isCollapsibleSystemNotification), NOT on
 * `senderType === 'system'`, so the content-aware acknowledgement (no templated
 * emailType) is never collapsed here — it falls through to the full-content
 * system-email branch.
 */
function CollapsibleEmailChip({
  message,
  label,
  recipientEmail,
  isHtmlContent,
}: {
  message: Message;
  label: string;
  recipientEmail?: string;
  isHtmlContent: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const subject = (message.metadata?.subject as string | undefined)?.trim();
  const time = formatDateTime(message.createdAt);

  return (
    <div className="flex justify-end py-2">
      {/* Collapsed pill stays compact (inline-flex). Expanded body uses the
          full conversation width — the wrapper is full-width, right-aligned. */}
      <div className="w-full min-w-0 flex flex-col items-end">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="group inline-flex max-w-full items-center gap-2 px-3 py-1.5 rounded-full text-left bg-[var(--co-green-wash)] border border-[var(--co-green-line)] text-[var(--co-green-ink)] hover:border-[var(--co-green)] transition-colors"
          title={expanded ? 'Hide email' : 'Show email'}
        >
          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[var(--co-green)]" />
          <span className="text-xs font-medium whitespace-nowrap">{label} sent</span>
          {recipientEmail && (
            <span className="text-xs text-[var(--co-ink-2)] truncate">
              to {recipientEmail}
            </span>
          )}
          {subject && (
            <span className="text-xs text-[var(--co-ink-3)] truncate hidden sm:inline">
              · {subject}
            </span>
          )}
          <span className="text-[10px] text-[var(--co-ink-3)] whitespace-nowrap hidden sm:inline">
            · {time}
          </span>
          <span className="inline-flex items-center gap-0.5 text-[11px] font-medium ml-1 flex-shrink-0">
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {expanded ? 'Hide' : 'Show'}
          </span>
        </button>
        {expanded && (
          <div className="mt-2 w-full max-w-full rounded-lg overflow-hidden bg-[var(--co-raised)] border border-[var(--co-green-line)]">
            {isHtmlContent ? (
              // The chip already gates this body behind a toggle, so don't also
              // length-clamp it — show the full confirmation once expanded.
              <EmailBody html={message.content} title={`${label} email`} collapsible={false} />
            ) : (
              <p className="px-4 py-3 text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {message.content}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Bubble surfaces, all theme tokens so both themes (and both appearances)
// repaint them. The agent side is the accent at wash strength — a filled
// accent bubble put a stock indigo slab in a theme that keeps its accent to
// ~5% coverage. The machine tone is reserved for what a model or the system
// produced; internal notes take the warn tone.
const BUBBLE = {
  agent: 'bg-[var(--co-brass-wash)] border border-[var(--co-brass-line)] text-[var(--co-ink)]',
  customer: 'bg-[var(--co-raised)] border border-[var(--co-line)] text-[var(--co-ink)]',
  machine: 'bg-[var(--co-machine-wash)] border border-[var(--co-machine-line)] text-[var(--co-ink)]',
  internal: 'bg-[var(--co-terracotta-wash)] border border-[var(--co-terracotta-line)] text-[var(--co-ink)]',
};

const CHANNEL_CHIP = 'text-[var(--co-ink-3)] bg-[var(--co-sunken)]';

const channelMeta: Record<string, { label: string; icon: ComponentType<{ className?: string }>; color: string; borderAgent: string; borderCustomer: string }> = {
  email: { label: 'Email', icon: Mail, color: CHANNEL_CHIP, borderAgent: 'border-r-4 border-r-[var(--co-brass)]', borderCustomer: 'border-l-4 border-l-[var(--co-line-strong)]' },
  phone: { label: 'Voice', icon: Phone, color: CHANNEL_CHIP, borderAgent: '', borderCustomer: '' },
  voice: { label: 'Voice', icon: Phone, color: CHANNEL_CHIP, borderAgent: '', borderCustomer: '' },
};

/** Round avatar for a message no person wrote: AI replies, system email. */
function MachineAvatar({ icon: Icon }: { icon: ComponentType<{ className?: string }> }) {
  return (
    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[var(--co-machine-wash)] border border-[var(--co-machine-line)]">
      <Icon className="h-5 w-5 text-[var(--co-machine-ink)]" />
    </div>
  );
}

function MessageBubble({
  message,
  ticketChannel,
  showEnglish = true,
  contactEmail,
}: {
  message: Message;
  ticketChannel?: string;
  showEnglish?: boolean;
  contactEmail?: string;
}) {
  // Null for every typed message, so email and chat rendering is untouched.
  const voiceTurn = readVoiceTurn(message);
  const inlineImageUrls = useInlineImageUrls(message);
  const isAgent = message.senderType === 'agent';
  const isSystem = message.senderType === 'system';
  const isChatOrigin = message.metadata?.source === 'chat';
  const isHtmlContent = message.contentType === 'html' ||
                        message.contentType?.toString().toUpperCase() === 'HTML' ||
                        (message.content && message.content.includes('<p>'));
  const isMarkdownContent = message.contentType === 'markdown';
  const isBot = message.senderType === 'bot' ||
                (isHtmlContent && message.content?.includes('knowledge base'));

  const channel = getMessageChannel(message, ticketChannel);
  const isEmail = channel === 'email';
  const isVoice = channel === 'phone' || channel === 'voice';
  const isChat = !isEmail && !isVoice;
  const meta = channelMeta[channel];

  // System email messages - display with full content
  const emailType = message.metadata?.emailType as string | undefined;
  const systemEmailSent = message.metadata?.systemEmailSent as boolean | undefined;
  const recipientEmail = message.metadata?.recipientEmail as string | undefined;
  const asAddressList = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((a): a is string => typeof a === 'string' && a.length > 0) : [];
  const emailTo = asAddressList(message.metadata?.to);
  const emailCc = asAddressList(message.metadata?.cc);
  // Inbound email from someone other than the ticket contact.
  const fromAddress = typeof message.metadata?.from === 'string' ? (message.metadata.from as string) : '';
  const otherSender =
    message.senderType === 'customer' &&
    !!contactEmail &&
    !!fromAddress &&
    fromAddress.trim().toLowerCase() !== contactEmail.trim().toLowerCase()
      ? fromAddress
      : '';
  const customerSentiment = message.metadata?.sentiment as MessageSentiment | undefined;

  const emailTypeLabels: Record<string, string> = {
    'ticket_confirmation': 'Ticket Confirmation',
    'ticket_update': 'Status Update',
    'sla_breach': 'SLA Alert',
    'ticket_resolved': 'Resolution Notice',
    'ticket_creation_failed': 'Creation Failed',
    'automation_reply': 'Automated Response',
    'csat_survey': 'Satisfaction Survey',
    'csat_request': 'Satisfaction Survey',
  };

  // Templated system notifications (allowlisted emailType) → compact,
  // expandable chip instead of the full email. Gated on the emailType
  // allowlist ONLY — the content-aware acknowledgement (system sender, no
  // templated emailType) is NOT matched here and stays fully visible below.
  if (isCollapsibleSystemNotification(message)) {
    return (
      <CollapsibleEmailChip
        message={message}
        label={emailTypeLabels[emailType || ''] || 'Notification'}
        recipientEmail={recipientEmail}
        isHtmlContent={!!isHtmlContent}
      />
    );
  }

  // System emails sent to customer - show with full content
  if (isSystem && (emailType || systemEmailSent)) {
    return (
      <div className="flex gap-3 py-4 flex-row-reverse">
        <div className="flex-shrink-0">
          <MachineAvatar icon={Mail} />
        </div>
        {/* Full-width: a system email is a real document in a sandboxed iframe,
            not a chat bubble — it must use the full conversation column. */}
        <div className="flex-1 min-w-0 flex flex-col items-end">
          <div className="flex items-center gap-2 mb-2 flex-row-reverse flex-wrap">
            <span className="text-sm font-semibold text-[var(--co-machine-ink)]">System</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatDateTime(message.createdAt)}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--co-machine-wash)] text-[var(--co-machine-ink)]">
              <Mail className="h-2.5 w-2.5" />
              {emailTypeLabels[emailType || ''] || 'System Email'}
            </span>
          </div>
          <div className="w-full min-w-0 rounded-lg overflow-hidden bg-[var(--co-raised)] border border-[var(--co-machine-line)] border-r-4 border-r-[var(--co-machine)]">
            {/* System email header badge */}
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--co-machine-wash)] border-b border-[var(--co-machine-line)]">
              <Mail className="h-4 w-4 text-[var(--co-machine-ink)]" />
              <span className="text-xs font-medium text-[var(--co-machine-ink)]">
                {emailTypeLabels[emailType || ''] || 'System Email'}
              </span>
              {recipientEmail && (
                <span className="text-xs text-[var(--co-ink-3)]">
                  sent to {recipientEmail}
                </span>
              )}
            </div>

            {/* Full email content — sandboxed iframe so the email's own
                CSS/backgrounds/full-width tables can't bleed into the panel. */}
            {isHtmlContent ? (
              <EmailBody html={message.content} title="System email" />
            ) : (
              <div className="px-4 py-3">
                <p className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">{message.content}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // System messages with HTML (AI responses) — render as bot
  if (isSystem && isHtmlContent) {
    return (
      <div className="flex gap-3 py-4 flex-row">
        <div className="flex-shrink-0">
          <MachineAvatar icon={Bot} />
        </div>
        <div className="max-w-[75%] flex flex-col items-start">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-[var(--co-machine-ink)]">AI Assistant</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatDateTime(message.createdAt)}
            </span>
          </div>
          <div className={clsx('rounded-2xl overflow-hidden p-4', BUBBLE.machine)}>
            <AIMessageContent content={message.content} />
          </div>
        </div>
      </div>
    );
  }

  // Plain system messages (notifications)
  if (isSystem) {
    return (
      <div className="flex justify-center py-3">
        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-4 py-1.5 rounded-full">
          {message.content}
        </div>
      </div>
    );
  }

  // --- Shared: header row ---
  const header = (
    <div className={clsx('flex items-center gap-2 mb-2 flex-wrap', isAgent ? 'flex-row-reverse' : 'flex-row')}>
      <span className={clsx(
        'text-sm font-semibold',
        isBot ? 'text-[var(--co-machine-ink)]' : 'text-gray-900 dark:text-white'
      )}>
        {message.senderName || (isAgent ? 'You' : isBot ? 'AI Assistant' : 'Customer')}
      </span>
      {isBot && isChatOrigin && (
        <span className="px-1.5 py-0.5 bg-[var(--co-machine-wash)] text-[var(--co-machine-ink)] text-[10px] font-medium rounded">AI</span>
      )}
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {formatDateTime(message.createdAt)}
      </span>
      {voiceTurn?.languageLabel && (
        // Labels a call that switched language part-way through, which the
        // caller's own words otherwise make hard to follow.
        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
          {voiceTurn.languageLabel}
        </span>
      )}
      {meta && (
        <span className={clsx('inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded', meta.color)}>
          <meta.icon className="h-2.5 w-2.5" />
          {meta.label}
        </span>
      )}
      <MessageEmotionChip sentiment={customerSentiment} />
      {message.isInternal && (
        <span className="inline-flex items-center gap-1 text-xs text-[var(--co-terracotta-ink)] bg-[var(--co-terracotta-wash)] px-2 py-0.5 rounded-full">
          <Lock className="h-3 w-3" /> Internal
        </span>
      )}
      {((isEmail && otherSender) || emailTo.length > 0 || emailCc.length > 0) && (
        // Who an email reply went to (multiple To / Cc), and who sent an inbound
        // one when it wasn't the contact. Full-width line under the name row;
        // absent on older replies, which went to the contact only.
        <span
          data-testid="message-recipients"
          className={clsx(
            'basis-full text-xs text-gray-500 dark:text-gray-400 break-all',
            isAgent ? 'text-right' : 'text-left',
          )}
        >
          {isEmail && otherSender && <>From: {otherSender}</>}
          {isEmail && otherSender && (emailTo.length > 0 || emailCc.length > 0) && ' · '}
          {emailTo.length > 0 && <>To: {emailTo.join(', ')}</>}
          {emailTo.length > 0 && emailCc.length > 0 && ' · '}
          {emailCc.length > 0 && <>Cc: {emailCc.join(', ')}</>}
        </span>
      )}
    </div>
  );

  // --- Shared: message content ---
  // Renders one slice of body content (used for both the visible body and the
  // trimmed quoted-history slice). Email HTML goes through the sandboxed iframe;
  // other HTML stays inline prose (chat HTML is small/benign).
  // Every bubble is a light surface in light mode now (none is an accent
  // fill), so body text never needs the inverted colours.
  const renderBodySlice = (slice: string) =>
  isEmail && isHtmlContent ? (
    <div
      className={clsx(
        'prose prose-sm max-w-none dark:prose-invert break-words overflow-x-auto',
        '[&_*]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:table-auto',
      )}
      dangerouslySetInnerHTML={{ __html: prepareMessageHtml(slice, message.attachments, inlineImageUrls) }}
    />
  ) : isHtmlContent ? (
      <div
        className={clsx(
          'prose prose-sm max-w-none dark:prose-invert break-words overflow-x-auto',
          '[&_*]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:table-auto',
        )}
        dangerouslySetInnerHTML={{ __html: prepareMessageHtml(slice, message.attachments, inlineImageUrls) }}
      />
    ) : (
      <p className="text-sm whitespace-pre-wrap text-[var(--co-ink)]">
        {slice}
      </p>
    );

  const renderContent = () => {
    // Bot/markdown + bot/html keep their bespoke renderers (no quote-trimming).
    if (isBot && isMarkdownContent) {
      return <div><MarkdownRenderer content={message.content} className="text-sm leading-relaxed" /></div>;
    }
    if (isBot && isHtmlContent) {
      return <AIMessageContent content={message.content} />;
    }
    // Normal customer/agent/system bodies: trim trailing quoted history /
    // signatures behind a Gmail-style toggle.
    return (
      <TrimmableContent
        message={message}
        isHtml={!!isHtmlContent}
        renderHtml={(html) => renderBodySlice(html)}
        renderText={(text) => renderBodySlice(text)}
      />
    );
  };

  // --- Shared: attachments ---
  const attachments = Array.isArray(message.attachments) && message.attachments.length > 0 ? (
    <div className="mt-3 pt-3 border-t border-[var(--co-line-subtle)] space-y-2">
      {message.attachments.map((attachment, idx) => {
        const att = attachment as typeof attachment & { fileId?: string };
        const href = att.fileId ? `/api/files/${att.fileId}/download` : att.url;
        return (
          <a key={idx} href={href} onClick={(e) => downloadMessageAttachment(e, att)}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-[var(--co-brass-ink)] hover:underline">
            <Paperclip className="h-3 w-3" />
            <span>{att.name}</span>
            {att.size && <span className="opacity-60">({Math.round(att.size / 1024)}KB)</span>}
          </a>
        );
      })}
    </div>
  ) : null;

  // ========== EMAIL: Card layout with left-right alignment ==========
  if (isEmail) {
    const borderColor = isAgent ? meta!.borderAgent : meta!.borderCustomer;

    return (
      <div className={clsx('flex gap-3 py-3', isAgent ? 'flex-row-reverse' : 'flex-row')}>
        <div className="flex-shrink-0">
          {isBot ? (
            <MachineAvatar icon={Bot} />
          ) : (
            <Avatar name={message.senderName || (isAgent ? 'Agent' : 'Customer')} size="md" />
          )}
        </div>
        <div className={clsx('flex-1 min-w-0 flex flex-col', isAgent ? 'items-end' : 'items-start')}>
          {header}
          {/* Email cards are capped at 85% and side-aligned (agent right /
              customer left, via the items-end/items-start parent) so the
              direction of each message reads at a glance. `w-full` is still
              required so the card has a concrete width for the iframe's
              width:100% to resolve against (without it the iframe falls back to
              its ~300px intrinsic width); max-w-[85%] just caps that width. */}
          <div className={clsx(
            'w-full max-w-[85%] min-w-0 rounded-lg overflow-hidden',
            message.isInternal ? BUBBLE.internal : BUBBLE.customer,
            borderColor,
          )}>
            {/* The sandboxed iframe supplies its own internal padding; the
                outer px/py keeps a clean inset frame around it and around the
                trim toggle / attachments. */}
            <div className="px-3 py-3 min-w-0">
              {renderContent()}
              {attachments}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== VOICE: Card with left-right alignment ==========
  if (isVoice) {
    const isRightSide = isAgent || isBot; // Bot (AI Voice Assistant) also goes right in voice calls
    return (
      <div className={clsx('flex gap-3 py-3', isRightSide ? 'flex-row-reverse' : 'flex-row')}>
        <div className="flex-shrink-0">
          {isBot ? (
            <MachineAvatar icon={Bot} />
          ) : (
            <Avatar name={message.senderName || (isAgent ? 'Agent' : 'Customer')} size="md" />
          )}
        </div>
        <div className={clsx('flex-1 min-w-0 flex flex-col', isRightSide ? 'items-end' : 'items-start')}>
          {header}
          <div className={clsx(
            'max-w-[85%] rounded-lg overflow-hidden',
            message.isInternal
              ? BUBBLE.internal
              : isBot
                ? BUBBLE.machine
                : isAgent
                  ? BUBBLE.agent
                  : BUBBLE.customer
          )}>
            <div className="px-4 py-3">
              {voiceTurn ? (
                <>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                    {displayedText(voiceTurn, showEnglish)}
                  </p>
                  {showsOriginalBeneath(voiceTurn, showEnglish) && (
                    // The original is never hidden, only demoted. An agent
                    // checking a disputed booking date needs what the caller
                    // actually said, not a machine's rendering of it.
                    <p className="mt-1.5 border-l-2 pl-2 text-xs leading-relaxed border-[var(--co-line-strong)] text-[var(--co-ink-3)]">
                      {voiceTurn.original}
                    </p>
                  )}
                </>
              ) : (
                renderContent()
              )}
              {attachments}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== CHAT: Bubble layout (current design) ==========
  return (
    <div className={clsx('flex gap-3 py-4', isAgent ? 'flex-row-reverse' : 'flex-row')}>
      <div className="flex-shrink-0">
        {isBot ? (
          <MachineAvatar icon={Bot} />
        ) : (
          <Avatar name={message.senderName || (isAgent ? 'Agent' : 'Customer')} size="md" />
        )}
      </div>
      <div className={clsx('max-w-[75%] flex flex-col', isAgent ? 'items-end' : 'items-start')}>
        {header}
        <div className={clsx(
          'rounded-2xl overflow-hidden',
          isAgent
            ? clsx(message.isInternal ? BUBBLE.internal : BUBBLE.agent, 'px-4 py-3')
            : isBot
            ? clsx(BUBBLE.machine, 'p-4')
            : clsx(BUBBLE.customer, 'px-4 py-3')
        )}>
          {voiceTurn ? (
            <>
              {isBot && isMarkdownContent ? (
                <MarkdownRenderer
                  content={displayedText(voiceTurn, showEnglish)}
                  className="text-sm leading-relaxed"
                />
              ) : (
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                  {displayedText(voiceTurn, showEnglish)}
                </p>
              )}
              {showsOriginalBeneath(voiceTurn, showEnglish) && (
                // Demoted, never hidden: an agent checking a disputed booking
                // date needs what the customer actually wrote.
                <p className="mt-1.5 border-l-2 pl-2 text-xs leading-relaxed border-[var(--co-line-strong)] text-[var(--co-ink-3)]">
                  {voiceTurn.original}
                </p>
              )}
            </>
          ) : (
            renderContent()
          )}
          {attachments}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a phase divider between chat-origin messages and post-ticket messages.
 */
function PhaseDivider({ label, icon: Icon }: { label: string; icon?: ComponentType<{ className?: string }> }) {
  const DividerIcon = Icon || MessageSquare;
  return (
    <div className="flex items-center gap-3 py-4">
      <div className="flex-1 border-t border-dashed border-gray-300 dark:border-gray-600" />
      <div className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">
        <DividerIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <div className="flex-1 border-t border-dashed border-gray-300 dark:border-gray-600" />
    </div>
  );
}

/**
 * A translation made on request, under the message it renders. The message
 * itself is left exactly as it was written - an email keeps its formatting,
 * and an agent checking a disputed detail still has the customer's own words.
 */
function RequestedTranslation({
  translation,
  alignRight,
}: {
  translation: MessageTranslation;
  alignRight: boolean;
}) {
  const from = languageName(translation.language);
  return (
    <div className={clsx('-mt-1 mb-3 flex', alignRight ? 'justify-end pr-[52px]' : 'pl-[52px]')}>
      <div className="max-w-[85%] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60">
        <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <Languages className="h-3 w-3" aria-hidden />
          In English{from && from !== translation.language ? ` · from ${from}` : ''} · machine translation
        </p>
        <p lang="en" className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800 dark:text-gray-200">
          {translation.text}
        </p>
        {translation.truncated && (
          <p className="mt-1 text-[11px] text-[var(--co-terracotta-ink)]">
            This message is long; only its beginning was translated.
          </p>
        )}
      </div>
    </div>
  );
}

const channelDividerConfig: Record<string, { label: string; icon: ComponentType<{ className?: string }> }> = {
  email: { label: 'Continued via Email', icon: Mail },
  phone: { label: 'Continued via Voice', icon: Phone },
  voice: { label: 'Continued via Voice', icon: Phone },
  chat: { label: 'Continued via Chat', icon: MessageCircle },
};

export function ConversationThread({
  messages,
  isLoading,
  isChatTicket,
  ticketChannel,
  voiceSummary,
  contactEmail,
  pendingReplies = [],
}: ConversationThreadProps) {
  // English by default when the call was not in English: an agent who cannot
  // read the caller's script gets something useful without touching a control.
  const [showEnglish, setShowEnglish] = useState(true);
  // Translations made on request, for messages that were stored without one
  // (email, and anything older than the stored renderings). By message id.
  const [translations, setTranslations] = useState<Record<string, MessageTranslation>>({});
  const [translating, setTranslating] = useState<{ done: number; total: number } | null>(null);
  const [translateFailures, setTranslateFailures] = useState(0);
  const translatingRef = useRef(false);
  const toTranslate = useMemo(() => untranslated(messages, translations), [messages, translations]);
  const hasTranslations = useMemo(
    () => threadHasTranslations(messages) || Object.keys(translations).length > 0,
    [messages, translations],
  );

  const translateThread = useCallback(async () => {
    // A ref as well as state: two quick clicks land before a re-render.
    if (translatingRef.current || toTranslate.length === 0) return;
    translatingRef.current = true;
    const pending = toTranslate;
    setTranslateFailures(0);
    setTranslating({ done: 0, total: pending.length });
    let failed = 0;
    // One at a time, oldest first: the translator is rate-limited, and each
    // message appears in English as soon as it is ready.
    for (let i = 0; i < pending.length; i++) {
      const message = pending[i];
      try {
        const result = await aiService.translateToEnglish(messagePlainText(message));
        setTranslations((prev) => ({
          ...prev,
          [message.id]: {
            text: result.translatedText,
            language: result.sourceLanguage,
            truncated: result.truncated,
          },
        }));
      } catch {
        failed += 1;
      }
      setTranslating({ done: i + 1, total: pending.length });
    }
    setTranslateFailures(failed);
    setTranslating(null);
    setShowEnglish(true);
    translatingRef.current = false;
  }, [toTranslate]);
  // Render chronological ASCENDING (oldest at top, newest at bottom). The
  // messages API already returns ascending order; this is a defensive STABLE
  // sort that preserves that order (and the backend's deliberate event-time
  // ordering) and never reverses it. See sortMessagesAscending.
  //
  // The ticket-confirmation system email is intentionally hidden from the
  // conversation — it's an automated acknowledgement, not part of the
  // agent/customer dialogue, so showing its green "Ticket Confirmation sent"
  // chip just adds noise. (Other system notifications like SLA/CSAT still show.)
  const orderedMessages = useMemo(
    () => filterDisplayableMessages(sortMessagesAscending(messages)),
    [messages],
  );

  if (isLoading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-[var(--co-brass)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-[var(--co-brass)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-[var(--co-brass)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }

  if (orderedMessages.length === 0 && pendingReplies.length === 0) {
    return (
      <div className="min-h-[200px] flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
          <Bot className="h-6 w-6 text-gray-400 dark:text-gray-500" />
        </div>
        <p className="text-sm">No messages in this conversation yet.</p>
      </div>
    );
  }

  let chatPhaseEnded = false;
  let prevChannel: string | null = null;

  return (
    <div className="px-4">
      {/* What the call was about, above the turns it is a summary of. */}
      {voiceSummary && (
        <div className={clsx('mt-4 rounded-lg p-3', BUBBLE.machine)}>
          <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-[var(--co-machine-ink)]">
            <Phone className="h-3.5 w-3.5" />
            What the call was about
          </div>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800 dark:text-gray-200">
            {voiceSummary}
          </p>
        </div>
      )}

      {/* Only when there is something in another script still to translate. */}
      {(toTranslate.length > 0 || translating || translateFailures > 0) && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          {translateFailures > 0 && !translating && (
            <span role="alert" className="text-xs text-[var(--co-burgundy)]">
              {translateFailures === 1
                ? '1 message could not be translated.'
                : `${translateFailures} messages could not be translated.`}
            </span>
          )}
          {(toTranslate.length > 0 || translating) && (
            <button
              type="button"
              onClick={translateThread}
              disabled={!!translating}
              aria-busy={!!translating}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-wait disabled:opacity-70 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {translating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Languages className="h-3.5 w-3.5" aria-hidden />
              )}
              {translating
                ? `Translating ${Math.min(translating.done + 1, translating.total)} of ${translating.total}…`
                : translateFailures > 0
                  ? 'Try again'
                  : 'Translate to English'}
            </button>
          )}
        </div>
      )}

      {/* Only when something was actually translated — a control that would
          do nothing should not appear. */}
      {hasTranslations && (
        <div className="mt-3 flex items-center justify-end gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Show turns in</span>
          <div className="inline-flex rounded-md border border-gray-200 p-0.5 dark:border-gray-700">
            {[
              { value: true, label: 'English' },
              {
                value: false,
                label:
                  ticketChannel === 'phone' || ticketChannel === 'voice' ? 'As spoken' : 'As written',
              },
            ].map((option) => (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => setShowEnglish(option.value)}
                className={clsx(
                  'rounded px-2.5 py-1 text-xs',
                  showEnglish === option.value
                    ? 'bg-gray-900 font-medium text-white dark:bg-gray-200 dark:text-gray-900'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {orderedMessages.map((message) => {
        const isChatOrigin = message.metadata?.source === 'chat';
        let showChatDivider = false;
        let showChannelDivider = false;
        let channelDivider: { label: string; icon: ComponentType<{ className?: string }> } | null = null;

        // Chat-to-ticket phase transition
        if (isChatTicket && !chatPhaseEnded && !isChatOrigin) {
          chatPhaseEnded = true;
          showChatDivider = true;
        }

        // Channel transition detection (skip system messages)
        if (message.senderType !== 'system') {
          const currentChannel = getMessageChannel(message, ticketChannel);
          if (prevChannel && currentChannel !== prevChannel) {
            const config = channelDividerConfig[currentChannel];
            if (config) {
              showChannelDivider = true;
              channelDivider = config;
            }
          }
          prevChannel = currentChannel;
        }

        return (
          <div key={message.id}>
            {showChatDivider && <PhaseDivider label="Ticket follow-up" />}
            {showChannelDivider && !showChatDivider && channelDivider && (
              <PhaseDivider label={channelDivider.label} icon={channelDivider.icon} />
            )}
            <MessageBubble message={message} ticketChannel={ticketChannel} showEnglish={showEnglish} contactEmail={contactEmail} />
            {showEnglish && translations[message.id] && (
              <RequestedTranslation
                translation={translations[message.id]}
                alignRight={message.senderType === 'agent'}
              />
            )}
          </div>
        );
      })}
      {pendingReplies.map(({ message, statusLabel }) => (
        <div
          key={message.id}
          data-testid="pending-reply"
          aria-label={`Reply not sent yet. ${statusLabel}`}
          className="opacity-80"
        >
          <MessageBubble message={message} ticketChannel={ticketChannel} contactEmail={contactEmail} />
          <div className="-mt-2 mb-2 flex justify-end pr-12">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--co-brass)] bg-[var(--co-brass-wash)] px-2.5 py-0.5 text-[11px] font-medium text-[var(--co-brass-ink)]">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              Not sent yet · {statusLabel}
            </span>
          </div>
        </div>
      ))}
      <div className="h-4" />
    </div>
  );
}

export default ConversationThread;
